import { useState } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import {
  exportAllFirestoreData,
  exportFirestoreCollection,
} from '../../services/exportToExcel';


const ExportFirestoreData = () => {
  const [exporting, setExporting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [exportingCategory, setExportingCategory] = useState(null);
  const [exportError, setExportError] = useState('');

  const exportCategories = [
    { label: 'Contacts', collection: 'contacts' },
    { label: 'Members', collection: 'members' },
    { label: 'Applications', collection: 'membershipApplications' },
    { label: 'Orders', collection: 'merchOrders' },
    { label: 'Partnerships', collection: 'partnershipInquiries' },
    { label: 'Volunteers', collection: 'volunteerApplications' },
  ];

  const handleCategoryExport = async (
  collectionName,
  categoryName
) => {
  try {
    setExportingCategory(collectionName);
    setExportError('');

    const result = await exportFirestoreCollection(
      collectionName
    );

    console.log(
      `${categoryName} exported successfully:`,
      result.count,
      'records'
    );
  } catch (error) {
    console.error(
      `Error exporting ${categoryName}:`,
      error
    );

    setExportError(
      `Failed to export ${categoryName}: ${
        error.message || 'Unknown error'
      }`
    );
  } finally {
    setExportingCategory(null);
  }
};

  const handleExport = async () => {
    try {
      setExporting(true);
      setSuccess('');
      setError('');

      await exportAllFirestoreData();

      setSuccess('Excel file downloaded successfully.');
    } catch (err) {
      console.error('Export error:', err);

      setError(
        err.message || 'Failed to export company data.'
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 2.5,
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ffffff 70%)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
            direction="row"
            spacing={1}
            sx={{
                mt: 1.5,
                flexWrap: 'wrap',
                gap: 0.75,
            }}
            >
            {exportCategories.map((category) => {
                const isExporting =
                exportingCategory === category.collection;

                return (
                <Chip
                    key={category.collection}
                    label={
                    isExporting
                        ? 'Downloading...'
                        : category.label
                    }
                    size="small"
                    variant="outlined"
                    clickable
                    disabled={exportingCategory !== null}
                    onClick={() =>
                    handleCategoryExport(
                        category.collection,
                        category.label
                    )
                    }
                    sx={{
                    borderColor: '#cbd5e1',
                    backgroundColor: '#fff',
                    fontSize: '0.75rem',
                    cursor:
                        exportingCategory !== null
                        ? 'default'
                        : 'pointer',
                    }}
                />
                );
            })}
            </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={2.5}>

          {/* Export information */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid',
              borderColor: '#e2e8f0',
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
            >
              <TableChartIcon
                sx={{
                  color: '#00796b',
                  mt: 0.2,
                }}
              />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                  }}
                >
                  Excel Workbook
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  Download contacts, members, applications,
                  orders, partnerships, and volunteer data
                  in a structured Excel workbook.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    mt: 1.5,
                    flexWrap: 'wrap',
                    gap: 0.75,
                  }}
                >
                  {exportCategories.map(({ label, collection }) => (
                    <Chip
                      key={collection}
                      label={label}
                      clickable
                      color={
                        exportingCategory === collection
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() =>
                        handleCategoryExport(collection, label)
                      }
                      disabled={Boolean(exportingCategory)}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Action area */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                Ready to export?
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                This may take a few moments depending on the
                amount of data.
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={
                exporting ? (
                  <CircularProgress
                    size={19}
                    color="inherit"
                  />
                ) : (
                  <FileDownloadIcon />
                )
              }
              onClick={handleExport}
              disabled={exporting}
              sx={{
                minWidth: 220,
                borderRadius: 2,
                px: 2.5,
                py: 1.2,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                backgroundColor: '#00796b',
                '&:hover': {
                  backgroundColor: '#00695c',
                  boxShadow: '0 4px 12px rgba(0, 121, 107, 0.2)',
                },
              }}
            >
              {exporting
                ? 'Exporting Data...'
                : 'Export All Data'}
            </Button>
          </Box>

          {/* Success message */}
          {success && (
            <Alert
                severity="success"
                sx={{
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                    fontWeight: 500,
                    },
                }}
                >
                {success}
            </Alert>
          )}

          {/* Error message */}
          {error && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}

        </Stack>
      </Box>
    </Paper>
  );
};

export default ExportFirestoreData;